<template>
  <v-container>
    <v-snackbar
      class="snackbar-top"
      v-model="notification.show"
      :top="true"
      :right="true"
      :color="notification.color"
    >
      {{ notification.message }}
      <v-btn
        fab small outlined
        @click="closeNotification"
      >
        <v-icon>clear</v-icon>
      </v-btn>
    </v-snackbar>

    <v-simple-table
      fixed-header
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{$t('forms.name')}}</th>
            <th class="text-left">{{$t('forms.email')}}</th>
            <th class="text-left">{{$t('forms.affiliation')}}</th>
            <th class="text-center">{{$t('forms.plan')}}</th>
            <th class="text-center">{{$t('forms.is_admin')}}</th>
            <th class="text-center">{{$t('forms.active')}}</th>
            <th class="text-center">
              <v-btn @click="addUser" fab small color="primary" :disabled="loading">
                <v-icon>add</v-icon>
              </v-btn>
              <v-btn @click="loadUsers" fab small color="primary" class="ml-2" :disabled="loading">
                <v-icon>refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td>{{ user.name }}</td>
            <td>{{ user.email }}</td>
            <td>{{ getDateBySeconds(user.affiliation_date) }}</td>
            <td class="text-center">
              <v-chip>{{ user.plan.name }}</v-chip>
            </td>
            <td class="text-center">
              <v-btn outlined fab small :color="user.admin ? 'primary' : 'red'" :disabled="loading">
                <v-icon v-if="user.admin">check</v-icon>
                <v-icon v-else>clear</v-icon>
              </v-btn>
            </td>
            <td class="text-center">
              <v-btn outlined fab small :color="user.active ? 'primary' : 'red'" @click="toggelActiveUser(user)" :disabled="loading">
                <v-icon v-if="user.active">check</v-icon>
                <v-icon v-else>clear</v-icon>
              </v-btn>
            </td>
            <td class="text-center">
              <v-btn @click="editUser(user)" fab small :disabled="loading">
                <v-icon>edit</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-dialog width="800" v-model="dialog_form">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          {{current_user.id ? $t('forms.edit-record', {name: $t('forms.user')}) : $t('forms.create-record', {name: $t('forms.user')})}}
        </v-card-title>
        <v-card-text>
          <v-alert type="error" dismissible v-if="error">{{error}}</v-alert>
          <v-alert type="success" dismissible v-if="notice">{{notice}}</v-alert>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="5">
                <span class="subtitle-1">{{$t('forms.affiliation-date')}}</span>
                <v-date-picker v-model="current_user.affiliation_date"></v-date-picker>
              </v-col>
              <v-col cols="7">
                <v-text-field
                  :label="$t('forms.email')"
                  tupe="text"
                  v-model="current_user.email"
                  :rules="emailRules"
                  required
                  :disabled="current_user.id ? true : false"
                ></v-text-field>

                <v-text-field
                  v-if="!current_user.id"
                  :label="$t('forms.password')"
                  type="password"
                  v-model="current_user.password"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('login.password')})]"
                  required
                ></v-text-field>

                <v-text-field
                  :label="$t('forms.name')"
                  type="text"
                  v-model="current_user.name"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.name')})]"
                  required
                ></v-text-field>

                <v-select
                  :label="$t('forms.plan')"
                  v-model="current_user.plan"
                  :items="plans"
                  item-text="name"
                  item-value="id"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.plan')})]"
                  required
                ></v-select>

                <v-switch
                  v-model="current_user.admin"
                  :label="$t('forms.is_admin')"
                ></v-switch>

                <v-switch
                  v-model="current_user.active"
                  :label="$t('forms.active')"
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveUser" :loading="loading" :disabled="loading">
            {{$t('forms.save')}}
            <template v-slot:loader>
              <span>{{$t('forms.loading-save')}}</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="!loaded" opacity="1" color="white" light absolute>
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<style lang="scss">
  .v-snack--top {
      top: 5em !important;
  }
</style>

<script>
import * as service from '@/store/service'

export default {
  data() {
    return {
      users: [],
      emailRules: [
        v =>
          !!v || this.$t("forms.is-required", { name: this.$t("login.email") }),
        v =>
          /.+@.+/.test(v) ||
          this.$t("forms.must-valid", { name: this.$t("forms.email") })
      ],
      valid: false,
      loaded: false,
      loading: false,
      error: null,
      notice: null,
      dialog_form: false,
      current_user: null,
      plans: [],
      notification: {
        color: 'gree',
        show: false,
        message: ''
      }
    }
  },
  created() {
    this.resetForm()
    this.loadUsers()
    this.loadPlans()
  },
  methods: {
    showNotification(success, message) {
      this.notification.message = message
      this.notification.color = success ? 'green' : 'red'
      this.notification.show = true
    },
    closeNotification() {
      this.notification.show = false
      this.notification.message = ''
    },
    loadPlans() {
      service.getPlans().then(result => {
        this.plans = result
      })
    },
    loadUsers() {
      this.loaded = false
      service.getUsers().then(result => {
        this.users = result
        this.loaded = true
      })
    },
    resetForm() {
      this.current_user = {
        id: null,
        name: '',
        email: '',
        password: '',
        plan: null,
        admin: false,
        active: true,
        affiliation_date: ''
      }

      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    openFormDialog() {
      this.dialog_form = true
    },
    closeFormDialog() {
      this.dialog_form = false
    },
    addUser() {
      this.resetForm()
      this.openFormDialog()
    },
    editUser(user) {
      this.resetForm()
      this.current_user = {
        id: user.id,
        name: user.name,
        email: user.email,
        plan: user.plan.id,
        admin: user.admin,
        active: user.active,
        affiliation_date: service.getDateBySeconds(user.affiliation_date, true)
      }
      this.openFormDialog()
    },
    saveUser() {
      if (this.$refs.form.validate()) {
        this.loading = true
        this.notice = null
        this.error = null

        if (this.current_user.affiliation_date === '') {
          this.error = this.$t('forms.custom-field-required', {name: this.$t('forms.affiliation-date')})
          this.loading = false
          return
        }

        let plan = service.getDocById('plans', this.current_user.plan)
        let affiliation_date_obj = new Date(this.current_user.affiliation_date + ' 00:00:00')
        let affiliation_date = service.getDateToFirestore(affiliation_date_obj)
        let user = {
          id: this.current_user.id,
          email: this.current_user.email,
          password: this.current_user.password,
          name: this.current_user.name,
          plan: plan,
          admin: this.current_user.admin,
          active: this.current_user.active,
          affiliation_date: affiliation_date
        }

        let action_save = this.current_user.id ? service.updateUser : service.createUser

        action_save(user)
        .then(() => {
          this.showNotification(true, this.$t("forms.saved-record"))
          this.closeFormDialog()
          this.$refs.form.reset()
          this.loading = false
          this.loaded = false
          this.loadUsers()
        })
        .catch(error => {
          this.error = error.message
          this.loading = false
        })
      }
    },
    toggelActiveUser(user) {
      this.loading = true
      service.toggleActiveUser(user)
      .then(() => {
        user.active = !user.active
        this.showNotification(true, this.$t('messages.success'))
        this.loading = false
      })
      .catch(error => {
        this.showNotification(false, error.message)
        this.loading = false
      })
    },
    getDateBySeconds(affiliation_date) {
      return service.getDateBySeconds(affiliation_date)
    }
  }
}
</script>
